import { apiClient } from './api'

/** Company portal (what an operator sees) lives under /portal; staff tooling under /operators. */
const PORTAL = '/api/cms/portal'
const ADMIN = '/api/cms/operators'

const unwrap = async <T,>(p: Promise<{ data: any }>): Promise<T> => (await p).data

// ---- types -----------------------------------------------------------------
export type OperatorStatus = 'pending_review' | 'active' | 'suspended' | 'rejected'
export type PayoutStatus = 'requested' | 'approved' | 'paid' | 'rejected' | 'failed' | 'cancelled'
export type LedgerType = 'earning' | 'refund' | 'payout' | 'payout_reversal' | 'adjustment' | 'penalty'

export interface Balances {
  available: number
  pending: number
  reservedForPayouts: number
  paidOut: number
  lifetimeGross: number
  lifetimeCommission: number
  lifetimeNet: number
}

export interface PortalMe {
  operator: {
    id: string; name: string; slug: string; status: OperatorStatus
    legalName: string | null; tin: string | null; businessLicenseNo: string | null
    contactName: string | null; contactEmail: string | null; contactPhone: string | null
    address: string | null; logoUrl: string | null
    holdDays: number; minPayout: number; createdAt: string; suspensionReason: string | null
  }
  membership: { role: 'owner' | 'manager' | 'accountant' }
  setup: {
    vehicles: number; approvedVehicles: number; drivers: number
    hasPayoutAccount: boolean; payoutAccountVerified: boolean; profileComplete: boolean
  }
}

export interface EarningsSummary {
  balances: Balances
  thisMonth: { rides: number; gross: number; commission: number; net: number }
  terms: {
    holdDays: number; minPayout: number; levyDeducted: boolean
    generalCommission: { percentage: number; fixedAmount: number; minAmount: number | null; maxAmount: number | null } | null
  }
  settlementActive: boolean
}

export interface LedgerRow {
  id: string; type: LedgerType; bookingId: string | null; description: string | null
  createdAt: string; occurredAt: string; availableAt: string; pending: boolean
  gross: number; commission: number; levyDeducted: number; net: number; basis: string | null
}

export interface PayoutRow {
  id: string; amount: number; status: PayoutStatus
  destination: { type: string; provider: string; accountName: string; accountMasked: string }
  externalRef: string | null; failureReason: string | null
  requestedAt: string; approvedAt: string | null; paidAt: string | null
  operator?: { id: string; name: string }
}

export interface PayoutAccount {
  id: string; type: 'mobile_money' | 'bank'; provider: string; accountName: string
  accountMasked: string; isDefault: boolean; verified: boolean; createdAt?: string
}

export interface Paged<T> { data: T[]; meta: { total: number; page: number; limit: number } }

export interface CommissionRule {
  id: string; name: string; operator: { id: string; name: string } | null
  routeId: string | null; routeName: string | null
  daladalaId: string | null; registrationNumber: string | null
  percentage: number; fixedAmount: number; minAmount: number | null; maxAmount: number | null
  priority: number; active: boolean; effectiveFrom: string; effectiveTo: string | null; notes: string | null
}

export interface VehicleDoc {
  id: string; type: string; typeLabel: string; fileName: string; mimeType: string; sizeBytes: number
  expiryDate: string | null; status: 'pending' | 'verified' | 'rejected' | 'expired'; rejectionReason: string | null; createdAt: string
}
export interface VehicleDocsPayload {
  documents: VehicleDoc[]
  checklist: Array<{ type: string; label: string; required: boolean; state: string }>
  missing: string[]
  ready: boolean
}

export interface AdminOverview {
  operators: { active: number; pending: number; suspended: number; rejected: number }
  pendingVehicles: number
  payouts: { open: number; openAmount: number }
  liability: { available: number; pending: number }
  settlement: { enabled: boolean; startAt: string | null }
  warnings: Array<{ code: string; message: string }>
}

export interface AdminOperatorRow {
  id: string; name: string; slug: string; status: OperatorStatus; isHouse: boolean
  contactName: string | null; contactEmail: string | null; contactPhone: string | null
  vehicles: number; employees: number; available: number; openPayouts: number; createdAt: string
}

export interface AdminOperatorDetail {
  operator: AdminOperatorRow & {
    legalName: string | null; tin: string | null; businessLicenseNo: string | null; address: string | null
    holdDays: number; minPayout: number; deductLevy: boolean; suspensionReason: string | null; approvedAt: string | null
  }
  members: Array<{ id: string; role: string; active: boolean; user: { id: string; firstName: string | null; lastName: string | null; email: string | null; phoneNumber: string | null } }>
  payoutAccounts: PayoutAccount[]
  balances: Balances
  fleet: Array<{ id: string; registrationNumber: string; make: string | null; model: string | null; totalSeats: number; status: string; approvalStatus: 'pending' | 'approved' | 'rejected'; approvalNotes: string | null; documentsReady: boolean; documentsMissing: string[]; documentsPending: number }>
}

// ---- company portal ----------------------------------------------------------
export const portal = {
  me: () => unwrap<{ data: PortalMe }>(apiClient.get(`${PORTAL}/me`)),
  updateMe: (body: Record<string, string | null>) => unwrap<any>(apiClient.patch(`${PORTAL}/me`, body)),
  team: () => unwrap<{ data: Array<{ id: string; role: string; active: boolean; user: any }> }>(apiClient.get(`${PORTAL}/team`)),
  summary: () => unwrap<{ data: EarningsSummary }>(apiClient.get(`${PORTAL}/earnings/summary`)),
  trend: (days: number) => unwrap<{ data: Array<{ date: string; gross: number; net: number; rides: number }> }>(apiClient.get(`${PORTAL}/earnings/trend`, { params: { days } })),
  ledger: (params: { page?: number; limit?: number; type?: string }) => unwrap<Paged<LedgerRow>>(apiClient.get(`${PORTAL}/earnings/ledger`, { params })),
  statementUrl: (from: string, to: string) => `${PORTAL}/earnings/statement.csv?from=${from}&to=${to}`,
  async downloadStatement(from: string, to: string) {
    const res = await apiClient.get(`${PORTAL}/earnings/statement.csv`, { params: { from, to }, responseType: 'blob' })
    const url = URL.createObjectURL(res.data)
    const a = document.createElement('a')
    a.href = url
    a.download = `statement-${from}-${to}.csv`
    a.click()
    URL.revokeObjectURL(url)
  },
  accounts: () => unwrap<{ data: PayoutAccount[] }>(apiClient.get(`${PORTAL}/payout-accounts`)),
  addAccount: (body: { type: string; provider: string; accountName: string; accountNumber: string }) => unwrap<any>(apiClient.post(`${PORTAL}/payout-accounts`, body)),
  removeAccount: (id: string) => unwrap<any>(apiClient.delete(`${PORTAL}/payout-accounts/${id}`)),
  payouts: (params: { page?: number; limit?: number }) => unwrap<Paged<PayoutRow>>(apiClient.get(`${PORTAL}/payouts`, { params })),
  requestPayout: (body: { accountId: string; amount: number }) => unwrap<any>(apiClient.post(`${PORTAL}/payouts`, body)),
  cancelPayout: (id: string) => unwrap<any>(apiClient.post(`${PORTAL}/payouts/${id}/cancel`)),
}

// ---- Daladala Smart staff ----------------------------------------------------
export const operatorsAdmin = {
  overview: () => unwrap<{ data: AdminOverview }>(apiClient.get(`${ADMIN}/overview`)),
  list: (params: { status?: string; search?: string; page?: number; limit?: number }) => unwrap<Paged<AdminOperatorRow>>(apiClient.get(ADMIN, { params })),
  create: (body: Record<string, unknown>) => unwrap<any>(apiClient.post(ADMIN, body)),
  detail: (id: string) => unwrap<{ data: AdminOperatorDetail }>(apiClient.get(`${ADMIN}/${id}`)),
  update: (id: string, body: Record<string, unknown>) => unwrap<any>(apiClient.patch(`${ADMIN}/${id}`, body)),
  setStatus: (id: string, action: string, reason?: string, tripPolicy?: 'cancel' | 'keep') => unwrap<{ data: { status: string; trips: CancelResult | null; tripsError: string | null } }>(apiClient.post(`${ADMIN}/${id}/status`, { action, reason, tripPolicy })),
  addMember: (id: string, body: Record<string, unknown>) => unwrap<any>(apiClient.post(`${ADMIN}/${id}/members`, body)),
  removeMember: (id: string, memberId: string) => unwrap<any>(apiClient.delete(`${ADMIN}/${id}/members/${memberId}`)),
  verifyAccount: (id: string, accountId: string) => unwrap<any>(apiClient.post(`${ADMIN}/${id}/payout-accounts/${accountId}/verify`)),
  ledger: (id: string, params: { page?: number; limit?: number }) => unwrap<Paged<LedgerRow & { createdById: string | null }>>(apiClient.get(`${ADMIN}/${id}/ledger`, { params })),
  adjust: (id: string, body: { amount: number; reason: string; kind: 'adjustment' | 'penalty'; requestId: string }) => unwrap<any>(apiClient.post(`${ADMIN}/${id}/adjustments`, body)),
  approveVehicle: (vehicleId: string, status: 'approved' | 'rejected', notes?: string, overrideDocuments = false) => unwrap<any>(apiClient.patch(`/api/cms/vehicles/${vehicleId}/approval`, { status, notes, overrideDocuments })),
  vehicleDocuments: (vehicleId: string) => unwrap<{ data: VehicleDocsPayload }>(apiClient.get(`/api/cms/vehicles/${vehicleId}/documents`)),
  reviewDocument: (vehicleId: string, docId: string, status: 'verified' | 'rejected', reason?: string) => unwrap<any>(apiClient.patch(`/api/cms/vehicles/${vehicleId}/documents/${docId}/review`, { status, reason })),
  async openDocument(vehicleId: string, docId: string) {
    const res = await apiClient.get(`/api/cms/vehicles/${vehicleId}/documents/${docId}/file`, { responseType: 'blob' })
    const url = URL.createObjectURL(res.data)
    window.open(url, '_blank', 'noopener')
    setTimeout(() => URL.revokeObjectURL(url), 60000)
  },

  rules: (operatorId?: string) => unwrap<{ data: CommissionRule[] }>(apiClient.get(`${ADMIN}/commission-rules`, { params: operatorId ? { operatorId } : {} })),
  createRule: (body: Record<string, unknown>) => unwrap<any>(apiClient.post(`${ADMIN}/commission-rules`, body)),
  updateRule: (id: string, body: Record<string, unknown>) => unwrap<any>(apiClient.patch(`${ADMIN}/commission-rules/${id}`, body)),
  retireRule: (id: string) => unwrap<any>(apiClient.delete(`${ADMIN}/commission-rules/${id}`)),
  preview: (body: Record<string, unknown>) => unwrap<{ data: { fare: number; commission: number; operatorGets: number; basis: string; settles: boolean } }>(apiClient.post(`${ADMIN}/commission-rules/preview`, body)),

  payouts: (params: { status?: string; operatorId?: string; page?: number; limit?: number }) => unwrap<Paged<PayoutRow>>(apiClient.get(`${ADMIN}/payouts`, { params })),
  payoutAction: (id: string, action: 'approve' | 'reject' | 'pay' | 'fail', body: { externalRef?: string; reason?: string } = {}) => unwrap<any>(apiClient.post(`${ADMIN}/payouts/${id}/${action}`, body)),
  revealDestination: (id: string) => unwrap<{ data: { type: string; provider: string; accountName: string; accountNumber: string } }>(apiClient.get(`${ADMIN}/payouts/${id}/destination`)),

  settlement: () => unwrap<{ data: { enabled: boolean; startAt: string | null } }>(apiClient.get(`${ADMIN}/settlement`)),
  saveSettlement: (body: { enabled: boolean; startAt: string | null }) => unwrap<any>(apiClient.put(`${ADMIN}/settlement`, body)),
  runSettlement: () => unwrap<{ data: { earnings: number; refunds: number; skippedNoRule: number; errors: number; skipped?: string } }>(apiClient.post(`${ADMIN}/settlement/run`)),
  audit: (params: { operatorId?: string; entityType?: string; page?: number; limit?: number }) => unwrap<Paged<any>>(apiClient.get(`${ADMIN}/audit`, { params })),
}

// ---- Control centre ----------------------------------------------------------
export type Tone = 'critical' | 'warning' | 'info'

export interface AttentionItem { id: string; tone: Tone; title: string; detail: string; count?: number; amount?: number; href: string; cta: string }

export interface ControlHome {
  attention: AttentionItem[]
  allClear: boolean
  kpis: { activeCompanies: number; liveBuses: number; ridesLast7d: number; commissionThisMonth: number; owedToCompanies: number; paidOutThisMonth: number }
  lastMoneyCheck: { at: string; status: 'clean' | 'issues' } | null
  activity: Array<{ id: string; action: string; summary: string | null; entityType: string; operatorId: string | null; actorRole: string | null; at: string }>
}

export interface InboxItem { id: string; kind: string; title: string; body: string | null; href: string | null; severity: 'info' | 'success' | 'warning' | 'critical'; createdAt: string; read: boolean }
export interface InboxPage { items: InboxItem[]; hasMore: boolean; unread: number }

export interface Impact {
  trips: number; emptyTrips: number; tripsWithPassengers: number; bookings: number; passengers: number; seats: number; refundTotal: number; boardingNow: number
  sample: Array<{ tripId: string; route: string; vehicle: string; departure: string; bookings: number; passengers: number; refund: number }>
}

export interface CancelResult {
  cancelledTrips: number; emptyTripsCancelled: number; keptTrips: number; bookingsCancelled: number; passengersNotified: number; refundTotal: number; boardingSkipped: number; failed: number
}

export interface RiskTrip {
  tripId: string; route: string; vehicle: string; vehicleId: string; departure: string | null; bookings: number; passengers: number; seats: number; refund: number
  operatorId: string | null; operatorName: string; operatorStatus: string
}

export interface Debt { operatorId: string; name: string; status: string; owes: number; lastPaidOutAt: string | null; lastRefundAt: string | null }

export interface Issue {
  severity: 'critical' | 'warning' | 'info'; code: string; title: string; detail: string
  count?: number; amount?: number; operatorId?: string; operatorName?: string; refs?: string[]
}

export interface LedgerReport {
  period: { from: string; to: string }
  summary: {
    ridesDelivered: number; fareDelivered: number; ridesSettled: number; grossSettled: number; commission: number; levyDeducted: number; netToOperators: number
    ridesUnsettled: number; fareUnsettled: number; promoAbsorbed: number; refundsPosted: number; paidOut: number; openPayouts: number; owedByCompanies: number
  }
  issues: Issue[]
  status: 'clean' | 'issues'
}

export interface GatewayReport {
  period: { from: string; to: string }
  summary: { statementRows: number; statementTotal: number; matched: number; matchedTotal: number; amountMismatches: number; missingFromOurs: number; missingFromStatement: number; missingTotal: number; failedButPaid: number }
  issues: Issue[]
  status: 'clean' | 'issues'
}

export interface SavedRun { id: string; kind: 'ledger' | 'gateway'; periodFrom: string; periodTo: string; status: 'clean' | 'issues'; summary: any; issues: Issue[]; createdAt: string }

export interface NoticeSetting { kind: string; label: string; group: 'company' | 'bus' | 'money' | 'staff' | 'system'; audience: 'staff' | 'operator'; external: boolean; default: boolean }

export interface Replacement { id: string; registrationNumber: string; totalSeats: number; operatorName: string; house: boolean }

export const control = {
  home: () => unwrap<{ data: ControlHome }>(apiClient.get(`${ADMIN}/control-center`)),

  inbox: (params: { unread?: boolean; before?: string; limit?: number } = {}) => unwrap<{ data: InboxPage }>(apiClient.get(`${ADMIN}/inbox`, { params: { ...params, unread: params.unread ? 'true' : undefined } })),
  unread: () => unwrap<{ data: { unread: number } }>(apiClient.get(`${ADMIN}/inbox/unread`)),
  markRead: (body: { ids: string[] } | { all: true }) => unwrap<{ data: { marked: number } }>(apiClient.post(`${ADMIN}/inbox/read`, body)),
  noticeSettings: () => unwrap<{ data: NoticeSetting[] }>(apiClient.get(`${ADMIN}/notification-settings`)),
  saveNoticeSettings: (external: Record<string, boolean>) => unwrap<{ data: NoticeSetting[] }>(apiClient.put(`${ADMIN}/notification-settings`, { external })),

  impact: (operatorId: string) => unwrap<{ data: Impact }>(apiClient.get(`${ADMIN}/${operatorId}/impact`)),
  atRisk: () => unwrap<{ data: RiskTrip[] }>(apiClient.get(`${ADMIN}/trips/at-risk`)),
  cancelTrip: (tripId: string, reason: string) => unwrap<{ data: CancelResult }>(apiClient.post(`${ADMIN}/trips/${tripId}/cancel`, { reason })),
  replacements: (tripId: string) => unwrap<{ data: Replacement[] }>(apiClient.get(`${ADMIN}/trips/${tripId}/replacements`)),
  reassign: (tripId: string, toVehicleId: string, reason?: string) => unwrap<{ data: { registrationNumber: string; passengersNotified: number } }>(apiClient.post(`${ADMIN}/trips/${tripId}/reassign`, { toVehicleId, reason })),

  debts: () => unwrap<{ data: Debt[] }>(apiClient.get(`${ADMIN}/debts`)),
  settleDebt: (operatorId: string, body: { mode: 'repayment' | 'writeoff'; amount: number; reference?: string; note?: string; requestId: string }) => unwrap<any>(apiClient.post(`${ADMIN}/${operatorId}/debt`, body)),

  runs: () => unwrap<{ data: SavedRun[] }>(apiClient.get(`${ADMIN}/reconciliation/runs`)),
  ledgerCheck: (body: { from?: string; to?: string }) => unwrap<{ data: LedgerReport }>(apiClient.post(`${ADMIN}/reconciliation/ledger`, body)),
  gatewayCheck: (body: { csv: string; from?: string; to?: string }) => unwrap<{ data: GatewayReport }>(apiClient.post(`${ADMIN}/reconciliation/gateway`, body)),

  bulkPay: (items: Array<{ id: string; reference: string }>) => unwrap<{ data: { results: Array<{ id: string; ok: boolean; error?: string }>; paid: number; failed: number } }>(apiClient.post(`${ADMIN}/payouts/bulk-pay`, { items })),
  async downloadPayoutSheet() {
    const res = await apiClient.get(`${ADMIN}/payouts/export`, { responseType: 'blob' })
    const url = URL.createObjectURL(res.data)
    const a = document.createElement('a')
    a.href = url
    a.download = `payouts-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    setTimeout(() => URL.revokeObjectURL(url), 10000)
  },
}

export type RequestStatus = 'new' | 'contacted' | 'documents_requested' | 'documents_received' | 'approved' | 'declined'

export interface PartnerRequest {
  id: string; companyName: string; contactName: string; email: string; phone: string; region: string | null; busCount: number | null
  routesInterest: string | null; message: string | null; status: RequestStatus; staffNotes: string | null; latraLicenceNo: string | null
  agreementSignedAt: string | null; declineReason: string | null; operatorId: string | null; createdAt: string; updatedAt: string
}

export const applications = {
  get: (id: string) => unwrap<{ data: PartnerRequest }>(apiClient.get(`${ADMIN}/applications/${id}`)),
  list: (params: { status?: string; q?: string; page?: number } = {}) =>
    unwrap<{ data: { items: PartnerRequest[]; total: number; page: number; limit: number; counts: Partial<Record<RequestStatus, number>> } }>(apiClient.get(`${ADMIN}/applications`, { params })),
  update: (id: string, body: { status?: RequestStatus; staffNotes?: string; latraLicenceNo?: string; agreementSigned?: boolean }) =>
    unwrap<{ data: PartnerRequest }>(apiClient.patch(`${ADMIN}/applications/${id}`, body)),
  approve: (id: string, body: { latraLicenceNo?: string; agreementSigned?: boolean }) =>
    unwrap<{ data: { operatorId: string; ownerEmail: string; temporaryPassword: string } }>(apiClient.post(`${ADMIN}/applications/${id}/approve`, body)),
  decline: (id: string, reason: string) => unwrap<{ data: PartnerRequest }>(apiClient.post(`${ADMIN}/applications/${id}/decline`, { reason })),
}
